import { useContext, useEffect, useRef, useState } from 'react';
import useSmartState from '../../../common/hooks/useSmartState';
import FormBuilder from '../../../library/Builders/FormBuilder';
import { SmartContext } from '../../../library/Core/SmartContext';
import SessionContext from '../../../library/Core/SessionContext';
import { isEmpty } from '../../../library/Core/SmartFunctions';
import { FormSection } from '../../../library/Core/SmartTypes';
import FiltersApplied from '../../../library/SimpleControls/FiltersApplied';
import BusinessDirectoryService from './BusinessDirectoryService';
import { toast } from 'react-toastify';

export const SearchCriteria = () => {
    const { state } = useContext(SmartContext);
    const { setStateValue } = useSmartState();
    const { sessionState } = useContext(SessionContext);
    const isAlumni = sessionState?.userTypeCode === 'ALUMNI';
    const searchFormConfig = state.formConfig?.sectionRepository[0] as FormSection;
    const [isExpanded, setIsExpanded] = useState(true);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [fuzzySearchText, setFuzzySearchText] = useState('');
    const viewMode = state.data?.viewMode ?? 'list';
    const searchCriteria = state.data?.searchCriteria || {};
    const previousCountryCodeRef = useRef(searchCriteria.countryCode || '');
    const fuzzySearchPlaceholder = searchFormConfig?.fuzzySearchPlaceholder || 'Search by Name, Service, or Location...';

    const filterSignature = JSON.stringify({
        fuzzySearchText: searchCriteria.fuzzySearchText,
        institutes: searchCriteria.institutes,
        category: searchCriteria.category,
        countryCode: searchCriteria.countryCode,
        cityCode: searchCriteria.cityCode,
        availability: searchCriteria.availability,
        experience: searchCriteria.experience,
        experience_min: searchCriteria.experience_min,
        experience_max: searchCriteria.experience_max,
        directoryType: searchCriteria.directoryType,
    });

    useEffect(() => {
        const currentCountryCode = searchCriteria.countryCode || '';
        const previousCountryCode = previousCountryCodeRef.current;

        // If country changes, clear city immediately and skip this cycle
        // so API is never called with stale cityCode.
        if (previousCountryCode !== currentCountryCode) {
            previousCountryCodeRef.current = currentCountryCode;
            if (searchCriteria.cityCode) {
                setStateValue('searchCriteria.cityCode', '' as any);
                return;
            }
        }

        if (isInitialLoad) {
            setIsInitialLoad(false);
            return;
        }

        const criteriaForSearch = {
            ...searchCriteria,
            page: 1,
        };

        if ((searchCriteria.page || 1) !== 1) {
            setStateValue('searchCriteria.page', 1 as any);
        }

        BusinessDirectoryService.search(criteriaForSearch)
            .then((response: any) => {
                const payload = response?.data ?? {};
                setStateValue('providerList', payload?.data ?? []);
                setStateValue('pagination', {
                    total: payload?.total ?? 0,
                    page: payload?.page ?? 1,
                    limit: payload?.limit ?? state.data.searchCriteria?.limit ?? 20,
                } as any);
                setStateValue('hasMore', ((payload?.page ?? 1) * (payload?.limit ?? 20)) < (payload?.total ?? 0) as any);
            })
            .catch((error) => {
                console.error('Error searching providers:', error);
            });
    }, [filterSignature]);

    const handleClearAll = () => {
        const defaultCriteria = BusinessDirectoryService.getDefaultSearchCriteria();
        setFuzzySearchText('');
        setStateValue('searchCriteria', defaultCriteria);
    };

    const handleFuzzySearch = (event: any) => {
        event.preventDefault();
        console.log('Fuzzy search text:', fuzzySearchText);
        const mergedCriteria = {
            ...searchCriteria,
            fuzzySearchText: fuzzySearchText?.trim(),
            page: 1,
        };

        setStateValue('searchCriteria', mergedCriteria as any);
    };

    const handleExportCSV = async () => {
        if (isExporting || isAlumni) return;
        setIsExporting(true);
        try {
            await BusinessDirectoryService.exportCSV(state.data.searchCriteria);
            toast.success('CSV exported successfully');
        } catch (error: any) {
            console.error('Export error:', error);
            // Handle row limit exceeded (400 from backend)
            const message = error?.response?.data?.message;
            if (message) {
                toast.error(message);
            } else {
                toast.error('Failed to export CSV. Please try again.');
            }
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="search_criteria mb-4">
            <div className="filter_section">
                <div className="row justify-content-between align-items-end flex-column-reverse flex-sm-row ">
                    <div className="col">
                        <div className="d-flex flex-wrap justify-content-end filterSearch">
                            <div className="d-flex flex-wrap flex-1 mb-0 justify-content-start">
                                <div className="filter_header cursor-pointer d-flex me-2" onClick={() => setIsExpanded(!isExpanded)}>
                                    <h5
                                        className={`font-16 font-600 mb-0  ${isExpanded ? 'rounded-4 rounded-bottom-0' : 'rounded-pill'} bg-gray-F2F4F7 px-4 py-2`}>
                                        <span className="py-1 d-block">
                                            <i className="bi bi-funnel-fill"></i> Filters
                                        </span>
                                    </h5>
                                    <span className={`collapse-icon ${isExpanded ? 'expanded' : ''}`}>
                                        <i className="icon-chevron-down"></i>
                                    </span>
                                </div>
                                <div className="search alumniSearch m-0 open position-static flex-1">
                                    <form className="form-inline d-flex flex-wrap justify-content-between d-block" onSubmit={handleFuzzySearch}>
                                        <input
                                            value={fuzzySearchText}
                                            placeholder={fuzzySearchPlaceholder}
                                            aria-label="Search"
                                            className="form-control flex-1 font-13 font-500 text-truncate"
                                            onChange={(event) => setFuzzySearchText(event?.target.value)}
                                        />
                                        <button className="btn btn-search icon icon-search" type="submit"></button>
                                    </form>
                                    <a href="javascript:;" className="btn-search-toggle icon-search"></a>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-auto">
                        <div className="row g-2 align-items-center mb-2">
                            <div className="col-auto">
                                <div className="view-toggle">
                                    <ul
                                        className="nav nav-pills list-switch-tab align-items-center d-md-flex"
                                        id="list-switch-tab"
                                        role="tablist">
                                        <li className="nav-item" role="presentation">
                                            <button
                                                className={`nav-link ${viewMode === 'list' ? 'active' : ''}`}
                                                id="pills-list-tab"
                                                type="button"
                                                role="tab"
                                                aria-controls="pills-list"
                                                aria-selected={viewMode === 'list'}
                                                onClick={() => setStateValue('viewMode', 'list' as any)}>
                                                <i className="bi bi-list me-1"></i> List
                                            </button>
                                        </li>
                                        <li className="nav-item" role="presentation">
                                            <button
                                                className={`nav-link ${viewMode === 'grid' ? 'active' : ''}`}
                                                id="pills-grid-tab"
                                                type="button"
                                                role="tab"
                                                aria-controls="pills-grid"
                                                aria-selected={viewMode === 'grid'}
                                                onClick={() => setStateValue('viewMode', 'grid' as any)}>
                                                <i className="bi bi-grid-fill me-1"></i> Grid
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div className="col-auto">
                                {!isAlumni && (
                                    <button
                                        onClick={handleExportCSV}
                                        disabled={isExporting}
                                        className="btn btn-skyblue w-auto height-40 d-inline-flex text-white font-14 text-decoration-none px-3"
                                        >
                                        {isExporting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                <span>Exporting...</span>
                                            </>
                                        ) : (
                                            <>
                                                {' '}
                                                <i className="bi bi-box-arrow-up-right font-16 me-1"></i> <span>Export</span>
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

          

                {isExpanded && (
                    <>
                        <div className="filter_body p-4 bg-gray-F2F4F7 rounded">
                            <FormBuilder key="searchCriteria" section={searchFormConfig} dataKey="searchCriteria" />
                        </div>

                        <div className="filter_actions p-3 d-flex gap-2">
                            <button className="btn btn-green btn-sm" onClick={handleClearAll}>
                                Clear All
                            </button>
                        </div>
                    </>
                )}
            </div>

            {!isEmpty(state.data.searchCriteria) && <FiltersApplied filters={state.data.searchCriteria} onClear={handleClearAll} />}
        </div>
    );
};
